import React from 'react';
import { Box, Button, Typography, Paper, Tooltip } from '@mui/material';
import { Copy, Check } from '@phosphor-icons/react';
import useCopyToClipboard from '../../../../hooks/useCopyToClipboard';

interface CodeBlockRendererProps {
  content: string;
  isMarkdown?: boolean;
}

const CodeBlockRenderer: React.FC<CodeBlockRendererProps> = ({
  content,
  //   isMarkdown = false,
}) => {
  const { copied, copy } = useCopyToClipboard();

  // Parse content to find code blocks
  const parseContent = (text: string) => {
    const codeBlockRegex = /```[\s\S]*?```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = codeBlockRegex.exec(text)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      // Add code block
      const codeContent = match[0]
        .replace(/^```[\w]*\n?/, '')
        .replace(/```$/, '');
      parts.push({
        type: 'code',
        content: codeContent,
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const handleCopyCodeBlock = (codeContent: string) => {
    copy(codeContent);
  };

  const parts = parseContent(content);

  return (
    <Box>
      {parts.map((part, index) => {
        if (part.type === 'code') {
          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                position: 'relative',
                border: '1px solid #DCE0E7',
                borderRadius: '8px',
                mb: 2,
                backgroundColor: '#f5f5f5',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1,
                  borderBottom: '1px solid #DCE0E7',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '12px',
                    color: '#6B6F80',
                    fontWeight: 500,
                  }}
                >
                  Code Block
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy code block'}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      copied ? <Check size={14} /> : <Copy size={14} />
                    }
                    onClick={() => handleCopyCodeBlock(part.content)}
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      borderRadius: '4px',
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      borderColor: copied ? '#4CAF50' : '#DCE0E7',
                      color: copied ? '#4CAF50' : '#6B6F80',
                      '&:hover': {
                        borderColor: copied ? '#4CAF50' : '#A8B2C7',
                        backgroundColor: copied ? '#f1f8e9' : '#f8f9fa',
                      },
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#302B3F',
                  backgroundColor: '#f5f5f5',
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                {part.content}
              </Box>
            </Paper>
          );
        } else {
          // Regular text content
          return (
            <Typography
              key={index}
              sx={{
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#302B3F',
                mb: part.content.trim() ? 2 : 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {part.content}
            </Typography>
          );
        }
      })}
    </Box>
  );
};

export default CodeBlockRenderer;
